import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { authHeaders, StatusChip, fmtDate, todayISO, Pager } from './instrShared';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Spinner, Plus, Wrench, Storefront } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PAGE_SIZE = 8;

const ACTIVITY_OPTIONS = [
  { value: 'clean', label: 'Cleaning' },
  { value: 'replace', label: 'Replacement' },
  { value: 'inspect', label: 'Inspection' },
  { value: 'other', label: 'Other' },
];

export const InstrMaintenanceTab = () => {
  const [boards, setBoards] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({ store_id: '', component: '', action_type: '', performed_at: todayISO(), note: '' });
  const [saving, setSaving] = useState(false);
  const [logPage, setLogPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, lg] = await Promise.all([
        axios.get(`${API_URL}/api/instrumentation/maintenance-status-all`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/instrumentation/maintenance-logs?limit=200`, { headers: authHeaders() }),
      ]);
      setBoards(Array.isArray(st.data) ? st.data : []);
      setLogs(Array.isArray(lg.data) ? lg.data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load maintenance data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const components = boards[0]?.rows?.map((r) => r.component) || [];

  const openLog = (storeId = '', component = '') => {
    setLogForm({
      store_id: storeId || (boards.length === 1 ? boards[0].store_id : ''),
      component,
      action_type: '',
      performed_at: todayISO(),
      note: '',
    });
    setLogOpen(true);
  };

  const submitLog = async () => {
    if (!logForm.store_id) { toast.error('Select a store'); return; }
    if (!logForm.component) { toast.error('Select a component'); return; }
    if (!logForm.action_type) { toast.error('Select the activity performed'); return; }
    setSaving(true);
    try {
      const store = boards.find((b) => b.store_id === logForm.store_id);
      await axios.post(`${API_URL}/api/instrumentation/maintenance-logs`, {
        store_id: logForm.store_id,
        store_name: store?.store_name || '',
        component: logForm.component,
        action_type: logForm.action_type,
        performed_at: logForm.performed_at,
        note: logForm.note,
      }, { headers: authHeaders() });
      toast.success('Maintenance logged');
      setLogOpen(false);
      setLogPage(1);
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to log maintenance'));
    } finally {
      setSaving(false);
    }
  };

  const logTotal = Math.max(1, Math.ceil(logs.length / PAGE_SIZE));
  const logSafe = Math.min(logPage, logTotal);
  const logPaged = logs.slice((logSafe - 1) * PAGE_SIZE, logSafe * PAGE_SIZE);

  return (
    <div className="space-y-5" data-testid="instr-maintenance-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
            <Wrench size={16} weight="fill" />
          </span>
          <p className="text-[13px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Maintenance
          </p>
        </div>
        <Button
          onClick={() => openLog()}
          disabled={boards.length === 0}
          className="h-9 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
          data-testid="maint-log-btn"
        >
          <Plus size={14} className="mr-2" /> Log Maintenance
        </Button>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Spinner size={22} className="animate-spin text-[#0A2540] inline" /></div>
      ) : boards.length === 0 ? (
        <p className="text-sm text-[#78716C] py-16 text-center">No stores found for your entity.</p>
      ) : (
        <>
          {/* Recent logs */}
          <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden" data-testid="maint-logs-panel">
            <div className="grid grid-cols-[120px_1fr_1fr_130px_1fr] gap-2 items-center px-4 py-2.5 bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">
              <span>Date</span>
              <span>Store Name</span>
              <span>Component</span>
              <span>Activity</span>
              <span>Performed By</span>
            </div>
            {logs.length === 0 ? (
              <p className="text-xs text-[#78716C] py-10 text-center">No maintenance logged yet.</p>
            ) : (
              <div className="divide-y divide-[#EDE6D8]">
                {logPaged.map((l) => (
                  <div key={l.id} className="grid grid-cols-[120px_1fr_1fr_130px_1fr] gap-2 items-center px-4 py-3" data-testid={`maint-log-${l.id}`}>
                    <span className="text-xs font-mono text-[#78716C]">{fmtDate(l.performed_at)}</span>
                    <span className="text-sm text-[#1C1917] truncate">{l.store_name || '—'}</span>
                    <span className="text-sm font-medium text-[#1C1917] truncate">{l.component}</span>
                    <span className="text-xs text-[#78716C]">{ACTIVITY_OPTIONS.find((a) => a.value === l.action_type)?.label || l.action_type}</span>
                    <span className="text-sm text-[#78716C] truncate" title={l.note || ''}>{l.performed_by}{l.note ? ` — ${l.note}` : ''}</span>
                  </div>
                ))}
              </div>
            )}
            <Pager page={logSafe} totalPages={logTotal} count={logs.length} label="logs" onPage={setLogPage} testid="maint-log-pagination" />
          </div>

          {/* Status boards per store */}
          {boards.map((b) => (
            <div key={b.store_id} className="bg-white border border-[#EDE6D8] rounded-2xl overflow-x-auto" data-testid={`maint-status-board-${b.store_id}`}>
              <div className="flex items-center gap-2.5 px-4 pt-4 pb-2">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#C9A35B]/15 text-[#8a6d33]">
                  <Storefront size={14} weight="fill" />
                </span>
                <p className="text-xs uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {b.store_name}{b.campus_name ? ` · ${b.campus_name}` : ''}
                </p>
              </div>
              <div className="min-w-[980px]">
                <div className="grid grid-cols-[1.3fr_1fr_1fr_1fr_1fr_80px] gap-2 items-center px-4 py-2.5 bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">
                  <span>Component</span>
                  <span>Maintenance Action</span>
                  <span>Cleaning</span>
                  <span>Replacement</span>
                  <span>Inspection</span>
                  <span />
                </div>
                <div className="divide-y divide-[#EDE6D8]">
                  {(b.rows || []).map((r) => (
                    <div key={r.component} className="grid grid-cols-[1.3fr_1fr_1fr_1fr_1fr_80px] gap-2 items-center px-4 py-2.5" data-testid={`maint-row-${b.store_id}-${r.component.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div>
                        <p className="text-sm font-medium text-[#1C1917]">{r.component}</p>
                        {r.volume_trigger_liters && (
                          <p className="text-[10px] font-mono text-[#78716C]">vol. trigger {Number(r.volume_trigger_liters).toLocaleString()} L</p>
                        )}
                      </div>
                      <span className="text-xs text-[#78716C]">{r.maintenance_action || '—'}</span>
                      <ActivityCell a={r.clean} />
                      <ActivityCell a={r.replace} />
                      <ActivityCell a={r.inspection} />
                      <button
                        type="button"
                        onClick={() => openLog(b.store_id, r.component)}
                        className="text-xs font-semibold text-[#0A2540] border border-dashed border-[#E2E8E5] hover:border-[#0A2540] hover:bg-[#FCFAF6] rounded-lg px-2 py-1 transition justify-self-end"
                        data-testid={`maint-quick-log-${b.store_id}-${r.component.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        Log
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Log dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-md" data-testid="maint-log-dialog">
          <DialogHeader>
            <DialogTitle>Log Maintenance</DialogTitle>
            <DialogDescription>Record a maintenance activity performed at a store.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Store</span>
              <Select value={logForm.store_id} onValueChange={(v) => setLogForm((p) => ({ ...p, store_id: v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="maint-dialog-store">
                  <SelectValue placeholder="Select store…" />
                </SelectTrigger>
                <SelectContent>
                  {boards.map((b) => <SelectItem key={b.store_id} value={b.store_id}>{b.store_name}{b.campus_name ? ` · ${b.campus_name}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Component</span>
              <Select value={logForm.component} onValueChange={(v) => setLogForm((p) => ({ ...p, component: v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="maint-dialog-component">
                  <SelectValue placeholder="Select component" />
                </SelectTrigger>
                <SelectContent>
                  {components.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Activity</span>
                <Select value={logForm.action_type} onValueChange={(v) => setLogForm((p) => ({ ...p, action_type: v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="maint-dialog-activity">
                    <SelectValue placeholder="Select activity" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value} data-testid={`maint-activity-${a.value}`}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Date Performed</span>
                <Input
                  type="date"
                  max={todayISO()}
                  value={logForm.performed_at}
                  onChange={(e) => setLogForm((p) => ({ ...p, performed_at: e.target.value }))}
                  className="h-9 text-sm"
                  data-testid="maint-dialog-date"
                />
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Note (optional)</span>
              <Textarea
                value={logForm.note}
                onChange={(e) => setLogForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="e.g. Backwashed for 20 minutes, pressure normal"
                className="text-sm min-h-[70px]"
                data-testid="maint-dialog-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpen(false)} className="border-[#EDE6D8] rounded-lg" data-testid="maint-dialog-cancel">Cancel</Button>
            <Button
              onClick={submitLog}
              disabled={saving}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="maint-dialog-save"
            >
              {saving ? <Spinner size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
              Save Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function ActivityCell({ a }) {
  if (!a || a.status === 'na') return <StatusChip status="na" />;
  return (
    <div>
      <StatusChip status={a.status} />
      <p className="text-[10px] font-mono text-[#78716C] mt-0.5">
        {a.status === 'no_record'
          ? `every ${a.interval_days}d`
          : a.status === 'overdue'
            ? `${Math.abs(a.days_left)}d overdue`
            : `due ${fmtDate(a.due)}`}
      </p>
    </div>
  );
}
